import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  verifyDriver,
  getDriverRides,
  getDriverActiveRide,
  getDriverStats,
  getAvailableRides,
  setAvailability,
  unsetAvailability,
  getAvailabilityStatus,
  placeBid,
  getDriverBids,
  sendOtp,
  verifyOtp,
  setAuthToken,
  clearAuthToken,
} from "./services/api";
import UserProfile from "./components/UserProfile";

const App = () => {
  const [profile, setProfile] = useState(null);
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [availableRides, setAvailableRides] = useState([]);
  const [registered, setRegistered] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Note: activeRide, registered, loading are used in the component logic
  const [error, setError] = useState(null);
  const [availability, setAvailabilityState] = useState({ availability: false });
  const [stats, setStats] = useState(null);
  const [bidsModalOpen, setBidsModalOpen] = useState(false);
  const [driverBids, setDriverBids] = useState([]);
  const [bidRideId, setBidRideId] = useState(null);
  const [bidAmount, setBidAmount] = useState(20);
  const [readyBidAmounts, setReadyBidAmounts] = useState({}); // per-ride input
  const [sortReadyBy, setSortReadyBy] = useState("distance"); // distance | time
  // Browser email OTP login
  const [browserEmail, setBrowserEmail] = useState("");
  const [browserOtp, setBrowserOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [browserLoginError, setBrowserLoginError] = useState(null);
  const [isTelegramContext, setIsTelegramContext] = useState(true);

  const telegramWebApp = useMemo(() => (typeof window !== "undefined" ? window.Telegram?.WebApp : undefined), []);

  const loadData = useCallback(async (telegramId) => {
    if (!telegramId) return;
    setRefreshing(true);
    try {
      const [ridesRes, activeRes, availStatus, statsRes, availRides, bidsRes] = await Promise.all([
        getDriverRides(telegramId),
        getDriverActiveRide(telegramId),
        getAvailabilityStatus(telegramId),
        getDriverStats(telegramId),
        getAvailableRides(telegramId),
        getDriverBids(telegramId),
      ]);

      if (ridesRes?.success) setRides(ridesRes.data || []);
      if (activeRes?.success) setActiveRide(activeRes.data || null);
      if (availStatus?.success) setAvailabilityState({ ...availStatus.data, availability: !!availStatus.data?.availability });
      if (statsRes?.success) setStats(statsRes.data || null);
      if (availRides?.success) setAvailableRides(availRides.data || []);
      if (bidsRes?.success) setDriverBids(bidsRes.data || []);
    } catch (e) {
      setError(e.message || "Failed to load driver data");
    } finally {
      setRefreshing(false);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    if (!telegramWebApp || !telegramWebApp.initData) {
      setIsTelegramContext(false);
      setLoading(false);
      setError(null); // Clear any error when not in Telegram
      // Try restore browser session from storage
      try {
        const storedToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_driver_token") : null;
        const storedProfile = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_driver_profile") : null;
        const storedRegistered = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_driver_registered") : null;
        if (storedToken && storedProfile) {
          setAuthToken(storedToken);
          setProfile(JSON.parse(storedProfile));
          setRegistered(storedRegistered !== "false");
          setError(null);
          const profileData = JSON.parse(storedProfile);
          if (profileData?.telegramId) {
            await loadData(profileData.telegramId);
          }
        } else {
          // No stored session - ensure profile is null and error is cleared
          setProfile(null);
          setError(null);
        }
      } catch (_) {
        // On error, ensure we show OTP screen
        setProfile(null);
        setError(null);
      }
      return;
    }
    setIsTelegramContext(true);
    try {
      telegramWebApp.ready?.();
      telegramWebApp.expand?.();
      const initData = telegramWebApp.initData;
      const verification = await verifyDriver(initData);
      if (!verification?.success) throw new Error(verification?.error || "Unable to verify user");

      const profileData = verification.data?.profile;
      setProfile(profileData || null);
      setRegistered(Boolean(verification.data?.registered));

      if (profileData?.telegramId) {
        await loadData(profileData.telegramId);
      }
    } catch (e) {
      setError(e.message || "Unable to bootstrap");
    } finally {
      setLoading(false);
    }
  }, [loadData, telegramWebApp]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const requestCurrentLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, name: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}` });
      },
      (err) => reject(new Error(err?.message || "Location denied")),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 15_000 }
    );
  });

  const handleSetAvailability = async () => {
    try {
      setError(null);
      const initData = telegramWebApp?.initData;
      let geo;
      try {
        geo = await requestCurrentLocation();
      } catch (locErr) {
        const guidance = "Location permission denied. Open your phone Settings → Telegram → Location → Allow while using the app, then try again.";
        throw new Error(`${locErr.message}. ${guidance}`);
      }
      const res = await setAvailability(initData, { location: geo, radiusMiles: 15, durationHours: 6 });
      if (!res?.success) throw new Error(res?.error || "Failed to set availability");
      setAvailabilityState(res.data);
      if (profile?.telegramId) await loadData(profile.telegramId);
    } catch (e) {
      setError(e.message || "Failed to set availability");
    }
  };

  const handleUnsetAvailability = async () => {
    try {
      const initData = telegramWebApp?.initData;
      const res = await unsetAvailability(initData);
      if (!res?.success) throw new Error(res?.error || "Failed to unset availability");
      setAvailabilityState({ availability: false });
    } catch (e) {
      setError(e.message || "Failed to unset availability");
    }
  };

  const handleOpenBidModal = (rideId) => {
    setBidRideId(rideId);
    setBidAmount(readyBidAmounts[rideId] ?? 20);
    setBidsModalOpen(true);
  };

  const submitBid = async () => {
    if (!bidRideId) return;
    try {
      const initData = telegramWebApp?.initData;
      const amt = Number(bidAmount);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid bid amount");
      const res = await placeBid(initData, { rideId: bidRideId, amount: amt });
      if (!res?.success) throw new Error(res?.error || "Failed to place bid");
      setBidsModalOpen(false);
      setBidRideId(null);
      if (profile?.telegramId) await loadData(profile.telegramId);
    } catch (e) {
      setError(e.message || "Failed to place bid");
    }
  };

  const submitEditBid = async (bidId, rideId) => {
    try {
      const initData = telegramWebApp?.initData;
      const amt = Number(readyBidAmounts[rideId]);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid bid amount");
      // Reuse placeBid to create a new bid; backend expires previous active bid for same ride
      const res = await placeBid(initData, { rideId, amount: amt });
      if (!res?.success) throw new Error(res?.error || "Failed to update bid");
      if (profile?.telegramId) await loadData(profile.telegramId);
    } catch (e) {
      setError(e.message || "Failed to update bid");
    }
  };

  const sortedAvailableRides = useMemo(() => {
    const arr = [...(availableRides || [])];
    if (sortReadyBy === "time") {
      arr.sort((a, b) => new Date(a.timeOfRide) - new Date(b.timeOfRide));
    } else {
      arr.sort((a, b) => (a.driverDistanceMiles ?? Infinity) - (b.driverDistanceMiles ?? Infinity));
    }
    return arr;
  }, [availableRides, sortReadyBy]);

  const RideSummaryRow = ({ r, action }) => (
    <li className="rounded-xl border border-white/10 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm truncate">{r.pickupLocationName} → {r.dropLocationName}</div>
          <div className="text-xs text-white/60 space-x-3">
            <span>{new Date(r.timeOfRide).toLocaleString()}</span>
            {r.riderName && <span>Rider: {r.riderName}</span>}
            {typeof r.driverDistanceMiles === 'number' && (
              <span>From you: {r.driverDistanceMiles} mi</span>
            )}
            {typeof r.routeDistanceMiles === 'number' && (
              <span>Trip: {r.routeDistanceMiles} mi</span>
            )}
            {typeof r.routeDurationMinutes === 'number' && (
              <span>ETA: {r.routeDurationMinutes} min</span>
            )}
          </div>
        </div>
        {action}
      </div>
    </li>
  );

  const BidStatusModal = () => {
    if (!bidsModalOpen) return null;
    const tabs = ["waiting", "accepted", "declined", "expired"];
    const normalize = (s) => (s === "rejected" ? "declined" : s === "active" ? "waiting" : s);
    const grouped = tabs.reduce((acc, t) => ({ ...acc, [t]: [] }), {});
    for (const b of driverBids) {
      const key = normalize(b.status);
      (grouped[key] || (grouped[key] = [])).push(b);
    }
    const currentRide = sortedAvailableRides.find((r) => r.id === bidRideId) || driverBids.find((b) => b.ride?.id === bidRideId)?.ride;
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Your Bids</h3>
            <button className="bw-btn-ghost" onClick={() => setBidsModalOpen(false)}>Close</button>
          </div>

          {currentRide && (
            <div className="mb-3 text-xs text-white/70">
              <div className="font-medium text-white/90">{currentRide.pickupLocationName} → {currentRide.dropLocationName}</div>
              <div className="space-x-3">
                <span>{new Date(currentRide.timeOfRide).toLocaleString()}</span>
                {currentRide.riderName && <span>Rider: {currentRide.riderName}</span>}
                {typeof currentRide.driverDistanceMiles === 'number' && <span>From you: {currentRide.driverDistanceMiles} mi</span>}
                {typeof currentRide.routeDistanceMiles === 'number' && <span>Trip: {currentRide.routeDistanceMiles} mi</span>}
                {typeof currentRide.routeDurationMinutes === 'number' && <span>ETA: {currentRide.routeDurationMinutes} min</span>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mb-3">
            <input type="number" min="1" className="bw-input" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} placeholder="Enter bid amount" />
            <button className="bw-btn" onClick={submitBid} disabled={!bidRideId}>Submit Bid</button>
          </div>

          <div className="space-y-3 max-h-96 overflow-auto">
            {tabs.map((t) => (
              <div key={t}>
                <div className="text-xs uppercase tracking-wide text-white/60 mb-1">{t}</div>
                {grouped[t]?.length ? (
                  <ul className="space-y-1">
                    {grouped[t].map((b) => (
                      <li key={b.id} className="rounded-lg border border-white/10 p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm">${b.amount} • {b.ride?.pickupLocationName} → {b.ride?.dropLocationName}</div>
                            <div className="text-xs text-white/60 space-x-3">
                              <span>{new Date(b.createdAt).toLocaleString()}</span>
                              {b.ride?.riderName && <span>Rider: {b.ride.riderName}</span>}
                              {typeof b.ride?.driverDistanceMiles === 'number' && <span>From you: {b.ride.driverDistanceMiles} mi</span>}
                              {typeof b.ride?.routeDistanceMiles === 'number' && <span>Trip: {b.ride.routeDistanceMiles} mi</span>}
                              {typeof b.ride?.routeDurationMinutes === 'number' && <span>ETA: {b.ride.routeDurationMinutes} min</span>}
                            </div>
                          </div>
                          {t === 'waiting' && (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                className="bw-input w-24"
                                value={readyBidAmounts[b.ride?.id] ?? b.amount}
                                onChange={(e) => setReadyBidAmounts((s) => ({ ...s, [b.ride?.id]: e.target.value }))}
                              />
                              <button className="bw-btn" onClick={() => submitEditBid(b.id, b.ride?.id)}>Edit</button>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-white/50">No bids</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleBrowserLogout = () => {
    clearAuthToken();
    setProfile(null);
    setRides([]);
    setActiveRide(null);
    setAvailabilityState({ availability: false });
    setAvailableRides([]);
    setStats(null);
    setDriverBids([]);
    try {
      sessionStorage.removeItem("miniapp_driver_token");
      sessionStorage.removeItem("miniapp_driver_profile");
      sessionStorage.removeItem("miniapp_driver_registered");
    } catch (_) {}
  };

  // Browser email OTP login when opened outside Telegram and no profile
  if (!isTelegramContext && !profile) {
    // Clear any old error messages
    if (error) {
      setError(null);
    }
    const handleSendOtp = async (e) => {
      e.preventDefault();
      const email = browserEmail.trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setBrowserLoginError("Please enter a valid email address.");
        return;
      }
      setBrowserLoginError(null);
      setOtpSubmitting(true);
      try {
        const res = await sendOtp(email, "driver");
        if (res?.success) {
          setOtpSent(true);
          setBrowserOtp("");
          setBrowserLoginError(null);
        } else {
          setBrowserLoginError(res?.error || "Could not send code. Try again.");
        }
      } catch (err) {
        setBrowserLoginError(err?.message || "Could not send code. Try again.");
      } finally {
        setOtpSubmitting(false);
      }
    };

    const handleVerifyOtp = async (e) => {
      e.preventDefault();
      const email = browserEmail.trim().toLowerCase();
      const otp = String(browserOtp).trim();
      if (!email || !otp) {
        setBrowserLoginError("Please enter the code from your email.");
        return;
      }
      setBrowserLoginError(null);
      setOtpSubmitting(true);
      try {
        const res = await verifyOtp(email, otp, "driver");
        if (res?.success && res.token && res.data?.profile) {
          setAuthToken(res.token);
          setProfile(res.data.profile);
          setRides(res.data.rides || []);
          setRegistered(Boolean(res.data.registered));
          setError(null);
          try {
            sessionStorage.setItem("miniapp_driver_token", res.token);
            sessionStorage.setItem("miniapp_driver_profile", JSON.stringify(res.data.profile));
            sessionStorage.setItem("miniapp_driver_registered", String(res.data.registered !== false));
          } catch (_) {}
          if (res.data.profile?.telegramId) {
            await loadData(res.data.profile.telegramId);
          }
        } else {
          setBrowserLoginError(res?.error || "Invalid or expired code.");
        }
      } catch (err) {
        setBrowserLoginError(err?.message || "Invalid or expired code.");
      } finally {
        setOtpSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bw-dark-root p-6">
        <div className="mx-auto max-w-md space-y-6">
          <h1 className="font-display text-3xl font-bold text-center">RideEase Driver</h1>
          <p className="bw-muted text-center text-sm">
            Enter the email you added in the app when using it in Telegram to log in.
          </p>
          {browserLoginError && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {browserLoginError}
            </div>
          )}
          {!otpSent ? (
            <form onSubmit={handleSendOtp} className="space-y-3">
              <label className="block text-sm font-medium text-white/80">Email</label>
              <input
                type="email"
                className="bw-input w-full"
                value={browserEmail}
                onChange={(e) => setBrowserEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={otpSubmitting}
              />
              <button type="submit" className="bw-btn w-full" disabled={otpSubmitting}>
                {otpSubmitting ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <label className="block text-sm font-medium text-white/80">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="bw-input w-full"
                value={browserOtp}
                onChange={(e) => setBrowserOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                disabled={otpSubmitting}
              />
              <button type="submit" className="bw-btn w-full" disabled={otpSubmitting}>
                {otpSubmitting ? "Verifying…" : "Log in"}
              </button>
              <button
                type="button"
                className="bw-btn-ghost w-full text-sm"
                onClick={() => { setOtpSent(false); setBrowserOtp(""); setBrowserLoginError(null); }}
              >
                Use a different email
              </button>
            </form>
          )}
          <div className="bw-divider pt-6 mt-6 space-y-3 text-center">
            <p className="bw-muted text-sm">New to RideEase?</p>
            <a
              href="/signup"
              className="bw-btn w-full inline-block text-center"
            >
              Create an account on our website
            </a>
            <a
              href="/"
              className="bw-btn-ghost w-full inline-block text-center text-sm"
            >
              Visit homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bw-dark-root p-4 text-white/90">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="text-center space-y-3">
          <h1 className="font-display text-3xl font-bold tracking-tight">RideEase Driver</h1>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button type="button" className="bw-btn" onClick={() => loadData(profile?.telegramId)} disabled={refreshing}>Refresh</button>
            {availability?.availability ? (
              <button type="button" className="bw-btn-ghost" onClick={handleUnsetAvailability}>Go Unavailable</button>
            ) : (
              <button type="button" className="bw-btn" onClick={handleSetAvailability}>Go Available</button>
            )}
            <button type="button" className="bw-btn-ghost" onClick={() => setBidsModalOpen(true)}>Bids</button>
            {!isTelegramContext && (
              <button type="button" onClick={handleBrowserLogout} className="bw-btn-ghost text-white/70">
                Log out
              </button>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-white/60">
            <span>Sort Ready to Bid by:</span>
            <button className={`bw-btn-ghost ${sortReadyBy === 'distance' ? 'ring-1 ring-white/40' : ''}`} onClick={() => setSortReadyBy('distance')}>Distance</button>
            <button className={`bw-btn-ghost ${sortReadyBy === 'time' ? 'ring-1 ring-white/40' : ''}`} onClick={() => setSortReadyBy('time')}>Time</button>
          </div>
        </header>

        {error && (
          <div className="rounded-md border border-white/20 bg-transparent px-4 py-3 text-sm bw-muted italic">{error}</div>
        )}

        {/* Driver Profile */}
        <UserProfile profile={availability?.telegramId ? availability : profile} stats={stats} />

        {/* Ready to Bid */}
        <section className="space-y-2">
          <h2 className="font-display text-xl font-semibold">Ready to Bid</h2>
          {sortedAvailableRides?.length ? (
            <ul className="space-y-2">
              {sortedAvailableRides.map((r) => (
                <RideSummaryRow
                  key={r.id}
                  r={r}
                  action={
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        className="bw-input w-24"
                        value={readyBidAmounts[r.id] ?? ""}
                        onChange={(e) => setReadyBidAmounts((s) => ({ ...s, [r.id]: e.target.value }))}
                        placeholder="Amount"
                      />
                      <button className="bw-btn" onClick={() => handleOpenBidModal(r.id)}>Bid</button>
                    </div>
                  }
                />
              ))}
            </ul>
          ) : (
            <div className="text-white/60 text-sm">No rides ready to bid.</div>
          )}
        </section>

        {/* My Rides */}
        <section className="space-y-2">
          <h2 className="font-display text-xl font-semibold">My Rides</h2>
          {rides?.length ? (
            <ul className="space-y-2">
              {rides.map((r) => (
                <li key={r.id} className="rounded-xl border border-white/10 p-3">
                  <div className="text-sm">{r.pickupLocationName} → {r.dropLocationName}</div>
                  <div className="text-xs text-white/60 flex gap-3">
                    <span>Status: {r.status}</span>
                    <span>Bid: ${r.bid}</span>
                    <span>{new Date(r.timeOfRide).toLocaleString()}</span>
                    {r.riderName && <span>Rider: {r.riderName}</span>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-white/60 text-sm">No rides yet.</div>
          )}
        </section>
      </div>
      <BidStatusModal />
    </div>
  );
};

export default App;
